/**
 * Maintenance category utilities — single source of truth for the
 * category vocabulary used across maintenance_tasks.category,
 * offers.primary_category, and offer_block_templates.task_category.
 *
 * MAINTENANCE_CATEGORIES is the full master list (valid in DB).
 * MARKKU_SERVICE_CATEGORIES is the subset Markku offers as services
 * (used by ERP catalog, AI offer generator, offer editor).
 */

export const MAINTENANCE_CATEGORIES = [
  // Markku service categories
  'chimney',
  'hvac',
  'heat_pump',
  'roof',
  'yard',
  'safety',
  'cleaning',
  'home_maintenance',
  'other',
  // Resident-only buckets (asukassovellus voi luoda näihin tehtäviä)
  'windows_doors',
  'facade',
  'foundation',
  'interior',
  'electrical',
  'heating',
  'property_care',
  'water_sewer',
  // Legacy catch-alls kept for data compatibility
  'inspection',
  'maintenance',
  // Reserved for the __CUSTOM_TASK__ system record
  'custom',
] as const

export type MaintenanceCategory = (typeof MAINTENANCE_CATEGORIES)[number]

export const MARKKU_SERVICE_CATEGORIES = [
  'chimney',
  'hvac',
  'heat_pump',
  'roof',
  'yard',
  'safety',
  'cleaning',
  'home_maintenance',
  'other',
] as const satisfies readonly MaintenanceCategory[]

export type MarkkuServiceCategory = (typeof MARKKU_SERVICE_CATEGORIES)[number]

export const MAINTENANCE_CATEGORY_LABELS: Record<MaintenanceCategory, string> =
  {
    chimney: 'Nuohous ja savuhormit',
    hvac: 'Ilmanvaihto',
    heat_pump: 'Lämpöpumput',
    roof: 'Katto ja sadevesijärjestelmät',
    yard: 'Piha ja ulkoalueet',
    safety: 'Turvallisuus ja paloturva',
    cleaning: 'Siivous',
    home_maintenance: 'Kodin huolto ja tarkastukset',
    other: 'Muut',
    windows_doors: 'Ikkunat ja ovet',
    facade: 'Julkisivu',
    foundation: 'Perustukset ja salaojat',
    interior: 'Sisätilat ja kalusteet',
    electrical: 'Sähkö',
    heating: 'Lämmitys',
    property_care: 'Kiinteistönhoito',
    water_sewer: 'Vesi ja viemäri',
    inspection: 'Tarkastukset',
    maintenance: 'Yleishuolto',
    custom: 'Mukautettu',
  }

export const MAINTENANCE_CATEGORY_COLORS: Record<MaintenanceCategory, string> =
  {
    chimney: 'dark',
    hvac: 'grape',
    heat_pump: 'violet',
    roof: 'red',
    yard: 'green',
    safety: 'orange',
    cleaning: 'cyan',
    home_maintenance: 'blue',
    other: 'gray',
    windows_doors: 'teal',
    facade: 'orange',
    foundation: 'gray',
    interior: 'pink',
    electrical: 'yellow',
    heating: 'red',
    property_care: 'teal',
    water_sewer: 'cyan',
    inspection: 'indigo',
    maintenance: 'blue',
    custom: 'gray',
  }

/**
 * Get Finnish label for a maintenance category. Returns the raw value
 * for unknown strings so legacy/in-flight data renders something
 * readable instead of disappearing.
 */
export function getCategoryLabel(category?: string | null): string {
  if (!category) return 'Ei kohdetta'
  return (
    MAINTENANCE_CATEGORY_LABELS[category as MaintenanceCategory] ?? category
  )
}

/**
 * Get Mantine color name for a maintenance category.
 */
export function getCategoryColor(category?: string | null): string {
  if (!category) return 'gray'
  return MAINTENANCE_CATEGORY_COLORS[category as MaintenanceCategory] ?? 'gray'
}

/** Sentinel `maintenance_tasks.name` for ad-hoc visit tasks; real title lives in `visit_tasks.task_name`. */
export const CUSTOM_TASK_PLACEHOLDER = '__CUSTOM_TASK__'

export function getVisitTaskDisplayName({
  taskName,
  maintenanceTaskName,
  fallback = 'Mukautettu tehtävä',
}: {
  taskName?: string | null
  maintenanceTaskName?: string | null
  fallback?: string
}): string {
  for (const value of [taskName, maintenanceTaskName]) {
    if (value && value !== CUSTOM_TASK_PLACEHOLDER) return value
  }
  return fallback
}

/**
 * Resident app dropdown — full master list (sans `custom`, which is a
 * system-only value reserved for the __CUSTOM_TASK__ record).
 */
const residentCategoryOrder: MaintenanceCategory[] = [
  'chimney',
  'hvac',
  'heat_pump',
  'roof',
  'yard',
  'safety',
  'cleaning',
  'home_maintenance',
  'windows_doors',
  'facade',
  'foundation',
  'interior',
  'electrical',
  'inspection',
  'maintenance',
  'other',
]

export const categoryOptions = residentCategoryOrder.map((value) => ({
  value,
  label: MAINTENANCE_CATEGORY_LABELS[value],
}))

/**
 * ERP catalog + offer editor — Markku service offering subset only.
 */
export const markkuServiceCategoryOptions = MARKKU_SERVICE_CATEGORIES.map(
  (value) => ({
    value,
    label: MAINTENANCE_CATEGORY_LABELS[value],
  }),
)

/**
 * Season type (simplified form used across the app)
 */
export type Season = 'spring' | 'summer' | 'fall' | 'winter'

/**
 * Single source of truth for season month boundaries (1-based, inclusive).
 * Winter wraps the year end (Dec–Feb). Date math (backend seasonBounds)
 * derives its windows from this so the season calendar lives in one place.
 */
export const SEASON_MONTHS: Record<Season, { start: number; end: number }> = {
  spring: { start: 3, end: 5 },
  summer: { start: 6, end: 8 },
  fall: { start: 9, end: 11 },
  winter: { start: 12, end: 2 },
}

/**
 * Scheduling type for maintenance tasks (database enum)
 */
export type SchedulingType =
  | 'periodic'
  | 'annual'
  | 'seasonal_spring'
  | 'seasonal_fall'
  | 'seasonal_winter'
  | 'seasonal_summer'

/**
 * Get Finnish label for a season
 */
export function getSeasonLabel(season: Season): string {
  const labels: Record<Season, string> = {
    spring: 'Kevät',
    summer: 'Kesä',
    fall: 'Syksy',
    winter: 'Talvi',
  }
  return labels[season]
}

/**
 * Get month range for a season (Finnish)
 */
export function getSeasonMonthRange(season: Season): string {
  const ranges: Record<Season, string> = {
    spring: 'Maalis-Toukokuu',
    summer: 'Kesä-Elokuu',
    fall: 'Syys-Marraskuu',
    winter: 'Joulu-Helmikuu',
  }
  return ranges[season]
}

/**
 * Get Mantine color for a season
 */
export function getSeasonColor(season: Season): string {
  const colors: Record<Season, string> = {
    spring: 'green',
    summer: 'yellow',
    fall: 'orange',
    winter: 'cyan',
  }
  return colors[season]
}

/**
 * Map schedulingType enum to season
 */
export function getSeasonFromSchedulingType(
  schedulingType: string | null,
): Season | null {
  if (!schedulingType) return null

  const mapping: Record<string, Season> = {
    seasonal_spring: 'spring',
    seasonal_summer: 'summer',
    seasonal_fall: 'fall',
    seasonal_winter: 'winter',
  }

  return mapping[schedulingType] || null
}

/**
 * Get current season based on current month
 */
export function getCurrentSeason(): Season {
  const month = new Date().getMonth()
  if (month >= 2 && month <= 4) return 'spring' // Mar-May
  if (month >= 5 && month <= 7) return 'summer' // Jun-Aug
  if (month >= 8 && month <= 10) return 'fall' // Sep-Nov
  return 'winter' // Dec-Feb
}

/**
 * Get season from a date
 */
export function getSeasonFromDate(date: Date | string): Season {
  const d = typeof date === 'string' ? new Date(date) : date
  const month = d.getMonth() + 1

  if (month >= 3 && month <= 5) return 'spring'
  if (month >= 6 && month <= 8) return 'summer'
  if (month >= 9 && month <= 11) return 'fall'
  return 'winter'
}

/**
 * Get Finnish label for a scheduling type (for ERP/admin UI)
 */
export function getSchedulingTypeLabel(schedulingType?: string | null): string {
  const labels: Record<string, string> = {
    periodic: 'Toistuva',
    annual: 'Vuosittainen',
    seasonal_spring: 'Kevätkausi',
    seasonal_fall: 'Syyskausi',
    seasonal_winter: 'Talvikausi',
    seasonal_summer: 'Kesäkausi',
  }
  return schedulingType
    ? labels[schedulingType] || schedulingType
    : 'Ei määritelty'
}

/**
 * Get color for a scheduling type (Mantine color name)
 * Uses season-based colors for seasonal types
 */
export function getSchedulingTypeColor(schedulingType?: string | null): string {
  const colors: Record<string, string> = {
    periodic: 'blue',
    annual: 'grape',
    seasonal_spring: 'green',
    seasonal_fall: 'orange',
    seasonal_winter: 'cyan',
    seasonal_summer: 'yellow',
  }
  return schedulingType ? colors[schedulingType] || 'gray' : 'gray'
}

/**
 * Scheduling type options for select inputs
 */
const schedulingTypeOrder: SchedulingType[] = [
  'periodic',
  'annual',
  'seasonal_spring',
  'seasonal_summer',
  'seasonal_fall',
  'seasonal_winter',
]

export const schedulingTypeOptions = schedulingTypeOrder.map((type) => ({
  value: type,
  label: getSchedulingTypeLabel(type),
}))
